import { useEffect, useRef, useState } from "react";
import { createEngine, disposeEngine, parseModel, buildPart, applyTransform, fitAll, tweenTo, projectPoint, fetchModel, setSceneTheme, setFrontView, renderExtras } from "@/lib/threeManual";
import { ChevronLeft, ChevronRight, RotateCcw, Maximize2, MousePointerClick, Move3d, List, X } from "lucide-react";
import { KannLoader } from "@/components/KannLoader";

// Read-only, embeddable interactive viewer. `modelUrl(assetId)` -> absolute URL for the raw model bytes.
export default function ManualViewer({ config, modelUrl, productName = "", intro = "" }) {
  const mountRef = useRef(null);
  const engineRef = useRef(null);
  const hotspotRefs = useRef({});
  const [ready, setReady] = useState(false);
  const [loaderGone, setLoaderGone] = useState(false);
  const [error, setError] = useState("");
  const [stepIdx, setStepIdx] = useState(0);
  const [activeHot, setActiveHot] = useState(null);
  const [showIntro, setShowIntro] = useState(true);
  const [listOpen, setListOpen] = useState(false);
  const steps = config?.steps || [];
  const step = steps[stepIdx];

  useEffect(() => {
    let alive = true;
    const engine = createEngine(mountRef.current, { theme: config?.theme || "light" });
    engineRef.current = engine;
    (async () => {
      try {
        for (const meta of config.parts || []) {
          const data = await fetchModel(modelUrl(meta.assetId), meta.ext);
          if (!alive) return;
          const root = parseModel(meta.ext, data);
          const { outer, mat } = buildPart(root, meta.color);
          applyTransform(outer, meta.base);
          engine.scene.add(outer);
          engine.parts.push({ id: meta.id, outer, mat });
        }
        if (!alive) return;
        fitAll(engine, true);
        setSceneTheme(engine, config?.theme || "light");
        setFrontView(engine, (config?.views || {}).front);
        // frame the first step before revealing so nothing "pops" into place
        if ((config.steps || []).length) goTo(0, true);
        setReady(true);
      } catch (e) { if (alive) setError("Kon het 3D-model niet laden."); }
    })();

    engine.onFrame = () => {
      const st = steps[stepIdxRef.current];
      if (!st) return;
      (st.hotspots || []).forEach((hs) => {
        const el = hotspotRefs.current[hs.id];
        if (!el) return;
        const p = projectPoint(engine, hs.x, hs.y, hs.z);
        el.style.left = `${p.x}px`; el.style.top = `${p.y}px`;
        el.style.display = p.visible ? "block" : "none";
      });
    };
    return () => { alive = false; disposeEngine(engine); engineRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stepIdxRef = useRef(0);
  useEffect(() => { stepIdxRef.current = stepIdx; }, [stepIdx]);
  useEffect(() => { if (ready) { const t = setTimeout(() => setShowIntro(false), 4200); return () => clearTimeout(t); } }, [ready]);
  useEffect(() => { if (ready) { const t = setTimeout(() => setLoaderGone(true), 560); return () => clearTimeout(t); } }, [ready]);

  const goTo = (i, immediate = false) => {
    const engine = engineRef.current; if (!engine || !steps[i]) return;
    setActiveHot(null);
    setStepIdx(i);
    const st = steps[i];
    const eff = {};
    (config.parts || []).forEach((p) => {
      const t = (st.transforms && st.transforms[p.id]) || p.base || {};
      eff[p.id] = { ...t, visible: !(st.hidden && st.hidden[p.id]) };
      const ep = engine.parts.find((x) => x.id === p.id);
      if (ep) ep.mat.color.set((st.colors && st.colors[p.id]) || p.color || "#d9d9d9");
    });
    tweenTo(engine, st.cam, eff, immediate ? 1 : 1800);
    renderExtras(engine, st.dims || []);
  };

  const resetView = () => { const e = engineRef.current; if (e) fitAll(e, true); };
  const fullscreen = () => { const el = mountRef.current?.parentElement?.parentElement; if (el?.requestFullscreen) el.requestFullscreen().catch(() => {}); };

  return (
    <div className="relative w-full h-full flex flex-col md:flex-row bg-[#f6f6f4] text-[#232323]" data-testid="manual-viewer">
      {!loaderGone && !error && <KannLoader theme={config?.theme || "light"} fadeOut={ready} />}
      {/* Canvas */}
      <div className="relative flex-1 min-h-[280px]">
        <div ref={mountRef} onPointerDown={() => setShowIntro(false)} className="w-full h-full" data-testid="viewer-canvas" style={{ backgroundImage: "radial-gradient(#e3e3e0 1px, transparent 1px)", backgroundSize: "22px 22px" }} />
        {error && <div className="absolute inset-0 flex items-center justify-center text-sm text-red-500" data-testid="viewer-error">{error}</div>}

        {ready && (
          <div className={`absolute inset-0 flex items-center justify-center pointer-events-none transition-opacity duration-700 ${showIntro ? "opacity-100" : "opacity-0"}`} data-testid="viewer-intro">
            <div className="bg-black/75 text-white rounded-2xl px-7 py-5 flex flex-col items-center gap-2 shadow-xl backdrop-blur-sm">
              <Move3d className="w-9 h-9 text-[#DB960D] animate-pulse" />
              <div className="text-sm font-semibold">Sleep om te draaien · scroll om te zoomen</div>
              <div className="text-xs text-white/70">Bekijk het product van alle kanten</div>
            </div>
          </div>
        )}

        {ready && (
          <>
            <div className="absolute top-3 left-3 bg-black/70 text-white text-[11px] px-3 py-1.5 rounded-full flex items-center gap-1.5 pointer-events-none">
              <MousePointerClick className="w-3.5 h-3.5" /> Sleep om te draaien · scroll om te zoomen
            </div>
            <div className="absolute top-3 right-3 flex gap-1.5">
              <button onClick={resetView} data-testid="viewer-reset" title="Weergave herstellen" className="bg-white/90 hover:bg-white shadow rounded-full p-2 text-[#232323]"><RotateCcw className="w-4 h-4" /></button>
              <button onClick={fullscreen} data-testid="viewer-fullscreen" title="Volledig scherm" className="bg-white/90 hover:bg-white shadow rounded-full p-2 text-[#232323]"><Maximize2 className="w-4 h-4" /></button>
            </div>

            {/* Hotspots for current step */}
            {(step?.hotspots || []).map((hs) => (
              <div key={hs.id} ref={(el) => (hotspotRefs.current[hs.id] = el)} className="absolute z-10" style={{ transform: "translate(-50%,-50%)" }} data-testid={`viewer-hotspot-${hs.id}`}>
                <button onClick={() => setActiveHot(activeHot === hs.id ? null : hs.id)}
                  className={`w-6 h-6 rounded-full text-[11px] font-bold flex items-center justify-center shadow-md transition-transform hover:scale-110 ${activeHot === hs.id ? "bg-[#DB960D] text-white ring-2 ring-white" : "bg-white text-[#232323] ring-2 ring-[#DB960D]"}`}>
                  {hs.label ? String(hs.label).slice(0, 2) : "i"}
                </button>
                {activeHot === hs.id && (
                  <div className="absolute left-1/2 -translate-x-1/2 mt-2 w-52 bg-[#232323] text-white text-xs rounded-lg shadow-xl px-3 py-2.5 z-20" data-testid={`viewer-hotspot-pop-${hs.id}`}>
                    <div className="font-semibold text-[#DB960D] mb-0.5">{hs.label || "Info"}</div>
                    <div className="text-white/90 leading-relaxed">{hs.description || ""}</div>
                  </div>
                )}
              </div>
            ))}
          </>
        )}
      </div>

      {/* Steps panel */}
      <div className="relative w-full md:w-[340px] shrink-0 border-t md:border-t-0 md:border-l border-black/10 bg-white flex flex-col max-h-[48%] md:max-h-full">
        {/* Header */}
        <div className="px-5 pt-3.5 pb-3 border-b border-black/10 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] md:text-[11px] font-mono tracking-[0.22em] uppercase text-[#DB960D]">Interactieve handleiding</div>
            <div className="text-base md:text-lg font-bold leading-tight mt-0.5 truncate">{productName || "Product"}</div>
          </div>
          <button onClick={() => setListOpen(true)} data-testid="viewer-steps-toggle"
            className="md:hidden shrink-0 flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-black/15 active:bg-black/10">
            <List className="w-4 h-4" /> Stappen
          </button>
        </div>

        {/* Mobile: focused current step (title + full description) */}
        <div className="md:hidden flex-1 overflow-y-auto px-5 py-4" data-testid="viewer-current-step">
          {steps.length === 0 ? (
            <p className="text-sm text-[#6B6B6B]">{intro || "Geen stappen beschikbaar."}</p>
          ) : (
            <div className="flex items-start gap-3">
              <span className="shrink-0 w-9 h-9 rounded-lg bg-[#DB960D] text-white text-sm font-bold flex items-center justify-center">{String(stepIdx + 1).padStart(2, "0")}</span>
              <div className="min-w-0 pt-0.5">
                <h3 className="text-base font-bold leading-tight" data-testid="viewer-current-title">{step?.title || `Stap ${stepIdx + 1}`}</h3>
                {step?.description && <p className="text-sm mt-1.5 leading-relaxed text-[#4a4a4a]" data-testid="viewer-current-desc">{step.description}</p>}
              </div>
            </div>
          )}
        </div>

        {/* Desktop: full list */}
        <div className="hidden md:block flex-1 overflow-y-auto p-3 space-y-1.5" data-testid="viewer-steps">
          {steps.length === 0 && <p className="text-sm text-[#6B6B6B] p-2">{intro || "Geen stappen beschikbaar."}</p>}
          {steps.map((s, i) => (
            <button key={s.id || i} onClick={() => goTo(i)} data-testid={`viewer-step-${i}`}
              className={`w-full text-left rounded-lg px-3 py-2.5 flex gap-3 transition-colors ${i === stepIdx ? "bg-[#232323] text-white" : "hover:bg-black/5 text-[#232323]"}`}>
              <span className={`shrink-0 w-6 h-6 rounded-md text-xs font-bold flex items-center justify-center ${i === stepIdx ? "bg-[#DB960D] text-white" : "bg-[#232323] text-white"}`}>{String(i + 1).padStart(2, "0")}</span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold truncate">{s.title || `Stap ${i + 1}`}</span>
                {i === stepIdx && s.description && <span className="block text-xs mt-1 leading-relaxed text-white/85">{s.description}</span>}
              </span>
            </button>
          ))}
        </div>

        {/* Nav footer */}
        <div className="p-3 border-t border-black/10 flex items-center gap-2">
          <button onClick={() => goTo(Math.max(0, stepIdx - 1))} disabled={stepIdx === 0} data-testid="viewer-prev" className="flex-1 md:flex-none justify-center flex items-center gap-1 text-sm px-3 py-2.5 rounded-full border border-black/15 active:bg-black/10 hover:bg-black/5 disabled:opacity-40"><ChevronLeft className="w-4 h-4" /> Vorige</button>
          <span className="text-xs text-[#6B6B6B] tabular-nums shrink-0 px-1">{steps.length ? stepIdx + 1 : 0} / {steps.length}</span>
          <button onClick={() => goTo(Math.min(steps.length - 1, stepIdx + 1))} disabled={stepIdx >= steps.length - 1} data-testid="viewer-next" className="flex-1 md:flex-none justify-center flex items-center gap-1 text-sm px-3 py-2.5 rounded-full bg-[#232323] text-white hover:bg-black active:bg-black disabled:opacity-40">Volgende <ChevronRight className="w-4 h-4" /></button>
        </div>
        <a href="https://kannprint.com/" target="_blank" rel="noreferrer" data-testid="viewer-kann-link" className="flex px-5 py-2 md:py-2.5 border-t border-black/10 items-center justify-center gap-1.5 text-[11px] text-[#6B6B6B] hover:text-[#232323] transition-colors">
          <span>Powered by</span>
          <span className="logo-font text-base text-[#232323] leading-none">KANN<span className="text-[#DB960D]">.</span></span>
        </a>

        {/* Mobile: all-steps bottom sheet */}
        {listOpen && (
          <div className="md:hidden absolute inset-0 z-30 bg-white flex flex-col" data-testid="viewer-steps-sheet">
            <div className="px-5 py-3 border-b border-black/10 flex items-center justify-between">
              <span className="text-sm font-bold">Alle stappen · {steps.length}</span>
              <button onClick={() => setListOpen(false)} data-testid="viewer-steps-close" className="p-1.5 rounded-full active:bg-black/10 hover:bg-black/5"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
              {steps.map((s, i) => (
                <button key={s.id || i} onClick={() => { goTo(i); setListOpen(false); }} data-testid={`viewer-sheet-step-${i}`}
                  className={`w-full text-left rounded-lg px-3 py-3 flex gap-3 transition-colors ${i === stepIdx ? "bg-[#232323] text-white" : "active:bg-black/10 hover:bg-black/5 text-[#232323]"}`}>
                  <span className={`shrink-0 w-7 h-7 rounded-md text-xs font-bold flex items-center justify-center ${i === stepIdx ? "bg-[#DB960D] text-white" : "bg-[#232323] text-white"}`}>{String(i + 1).padStart(2, "0")}</span>
                  <span className="min-w-0 pt-0.5">
                    <span className="block text-sm font-semibold">{s.title || `Stap ${i + 1}`}</span>
                    {s.description && <span className="block text-xs mt-0.5 leading-snug text-current/70 line-clamp-2 opacity-70">{s.description}</span>}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
